/**
 * One-line summary for a schedule-comparison header ("Current" / "After" /
 * "New" columns of Make a Payment, Change Schedule and Skip Payment).
 *
 * Shared by the Stripe path (`PaymentScheduleDisplay`) and the NMI form
 * (`DeferaCapitalNmiMakePaymentForm`) so both rails describe a schedule the
 * same way. Lifted verbatim from the NMI form (commit 84a48326); the
 * formatting is the house one — en-US grouping, two decimals, "·" separator.
 */

const fmt = (cents) => (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "N × $X" only when every row really is $X. A past-due schedule mixes the
 *  reduced past-due remainder with regular installments (1OSUWO after $400:
 *  $289.81 then 4 × ~$752.98), and the server's `per_payment_cents` is the first
 *  row — so "5 × $289.81" would be a lie. The Stripe schedule endpoint produces
 *  mixed rows too (a partial-credit first row, a final-balance correction, a
 *  last cycle a cent short). Summarize those as "N payments · $total".
 *
 *  `summarizeNonUniform` is opt-in so callers that never see mixed rows keep
 *  rendering exactly as before. */
export function scheduleSummary(schedule, { summarizeNonUniform = false } = {}) {
  const rows = schedule?.dates || [];
  const num = schedule?.num_payments ?? rows.length;
  const perPayment = schedule?.per_payment_cents || 0;
  const uniform = rows.length === 0 || rows.every((d) => (d.amount_cents || 0) === (rows[0].amount_cents || 0));
  if (!summarizeNonUniform || uniform) return `${num} × $${fmt(perPayment)}`;
  const total = rows.reduce((sum, d) => sum + (d.amount_cents || 0), 0);
  return `${num} ${num === 1 ? 'payment' : 'payments'} · $${fmt(total)}`;
}
